import React from "react";
import { Group, Paper, Stack, Table, Text, Title } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { NPCTTestResult, NPCTSnapshot, NPCTUserInput } from "types/testTypes";
import TimeLineChart from "@components/common/TimeLineChart";
import { VerticalReferenceLine } from "types/uiTypes";
import NPCTGazeAngleChart from "./NPCTGazeAngleChart";
import { computeDValueTicks, formatUserInputD } from "./NPCTAxisHelpers";

// Match NPCTGazeAngleChart: left eye is blue, right eye is red.
const LEFT_EYE_COLOR = "blue";
const RIGHT_EYE_COLOR = "red";
const GAZE_ANGLE_AXIS_PROPS = {
  domain: [-40, 40],
  ticks: [-40, -30, -20, -10, 0, 10, 20, 30, 40],
  allowDataOverflow: true,
};

interface NPCTTestEntryProps {
  entry: NPCTTestResult | null;
}

function formatDistanceCm(distanceM: number): string {
  return (distanceM * 100).toFixed(1);
}

function formatDistanceWithD(distanceM: number): string {
  return `${formatDistanceCm(distanceM)} cm (D ${formatUserInputD(distanceM)})`;
}

function buildTriggerLines(
  userInputs: NPCTUserInput[],
  t: (key: string) => string,
): VerticalReferenceLine[] {
  return userInputs
    .filter((u) => u.isValid && u.distance > 0)
    .map((u) => {
      const baseLabel = u.isApproaching ? t("npctTriggerApproach") : t("npctTriggerRecede");
      return {
        x: u.timestamp,
        label: `${baseLabel} — D ${formatUserInputD(u.distance)}`,
        color: u.isApproaching ? "violet" : "orange",
        labelPosition: u.isApproaching ? "insideTopRight" : "insideBottomRight",
      };
    });
}

export default function NPCTTestEntry({ entry }: NPCTTestEntryProps) {
  const { t } = useTranslation();
  const noop = () => undefined;

  const results = entry?.results;
  const snapshots: NPCTSnapshot[] = React.useMemo(() => results?.snapshots ?? [], [results]);
  const userInputs: NPCTUserInput[] = React.useMemo(() => results?.userInputs ?? [], [results]);

  const triggerLines: VerticalReferenceLine[] = React.useMemo(
    () => buildTriggerLines(userInputs, t),
    [userInputs, t],
  );

  const leftGazeXData = React.useMemo(
    () =>
      snapshots
        .filter((s) => s.leftGazeAngles !== null)
        .map((s) => ({ time: s.timestamp, value: s.leftGazeAngles!.x })),
    [snapshots],
  );

  const leftGazeYData = React.useMemo(
    () =>
      snapshots
        .filter((s) => s.leftGazeAngles !== null)
        .map((s) => ({ time: s.timestamp, value: s.leftGazeAngles!.y })),
    [snapshots],
  );

  const rightGazeXData = React.useMemo(
    () =>
      snapshots
        .filter((s) => s.rightGazeAngles !== null)
        .map((s) => ({ time: s.timestamp, value: s.rightGazeAngles!.x })),
    [snapshots],
  );

  const rightGazeYData = React.useMemo(
    () =>
      snapshots
        .filter((s) => s.rightGazeAngles !== null)
        .map((s) => ({ time: s.timestamp, value: s.rightGazeAngles!.y })),
    [snapshots],
  );

  const dAxisProps = React.useMemo(() => {
    const { ticks, labels } = computeDValueTicks(snapshots);
    if (ticks.length === 0) return undefined;
    return {
      ticks,
      interval: 0,
      tickFormatter: (v: number) => labels.get(v) ?? "",
    };
  }, [snapshots]);

  const breakPoint = userInputs.find((u) => u.isValid && u.isApproaching);
  const recoveryPoint = userInputs.find((u) => u.isValid && !u.isApproaching);

  if (!entry) {
    return <Text>{t("common.noDataAvailable")}</Text>;
  }

  return (
    <Stack gap="md" mb="md">
      <Group align="flex-start" wrap="nowrap" gap="md">
        {snapshots.length > 0 && (
          <NPCTGazeAngleChart snapshots={snapshots} userInputs={userInputs} />
        )}

        <Stack gap="md" style={{ flex: 1, minWidth: 0 }}>
          <Paper p="md" radius="md" withBorder>
            <Title order={4} mb="md">
              {t("gazeAngles")}
            </Title>
            <Table striped highlightOnHover withTableBorder>
              <Table.Tbody>
                <Table.Tr>
                  <Table.Th>{t("npctBreakPoint")}</Table.Th>
                  <Table.Td>
                    {breakPoint != null && breakPoint.distance > 0
                      ? formatDistanceWithD(breakPoint.distance)
                      : "—"}
                  </Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Th>{t("npctRecoveryPoint")}</Table.Th>
                  <Table.Td>
                    {recoveryPoint != null && recoveryPoint.distance > 0
                      ? formatDistanceWithD(recoveryPoint.distance)
                      : "—"}
                  </Table.Td>
                </Table.Tr>
              </Table.Tbody>
            </Table>
          </Paper>

          {snapshots.length > 0 && (
            <Group align="flex-start" wrap="nowrap" gap="md" grow>
              <Paper p="md" radius="md" withBorder style={{ flex: 1, minWidth: 0 }}>
                <Title order={4} mb="md">
                  {t("npctLeftEyeGaze")}
                </Title>
                <Stack gap="md">
                  <TimeLineChart
                    data={leftGazeXData}
                    series={[{ color: LEFT_EYE_COLOR, label: t("npctLeftGazeX") }]}
                    verticalReferenceLines={triggerLines}
                    onHovered={noop}
                    defocus={noop}
                    title={t("npctLeftGazeX")}
                    tooltipProps={{ timeUnit: "s" }}
                    withDots={false}
                    withLegend={false}
                    curveType="linear"
                    xAxisProps={dAxisProps}
                    yAxisProps={GAZE_ANGLE_AXIS_PROPS}
                  />
                  <TimeLineChart
                    data={leftGazeYData}
                    series={[{ color: LEFT_EYE_COLOR, label: t("npctLeftGazeY") }]}
                    verticalReferenceLines={triggerLines}
                    onHovered={noop}
                    defocus={noop}
                    title={t("npctLeftGazeY")}
                    tooltipProps={{ timeUnit: "s" }}
                    withDots={false}
                    withLegend={false}
                    curveType="linear"
                    xAxisProps={dAxisProps}
                    yAxisProps={GAZE_ANGLE_AXIS_PROPS}
                  />
                </Stack>
              </Paper>

              <Paper p="md" radius="md" withBorder style={{ flex: 1, minWidth: 0 }}>
                <Title order={4} mb="md">
                  {t("npctRightEyeGaze")}
                </Title>
                <Stack gap="md">
                  <TimeLineChart
                    data={rightGazeXData}
                    series={[{ color: RIGHT_EYE_COLOR, label: t("npctRightGazeX") }]}
                    verticalReferenceLines={triggerLines}
                    onHovered={noop}
                    defocus={noop}
                    title={t("npctRightGazeX")}
                    tooltipProps={{ timeUnit: "s" }}
                    withDots={false}
                    withLegend={false}
                    curveType="linear"
                    xAxisProps={dAxisProps}
                    yAxisProps={GAZE_ANGLE_AXIS_PROPS}
                  />
                  <TimeLineChart
                    data={rightGazeYData}
                    series={[{ color: RIGHT_EYE_COLOR, label: t("npctRightGazeY") }]}
                    verticalReferenceLines={triggerLines}
                    onHovered={noop}
                    defocus={noop}
                    title={t("npctRightGazeY")}
                    tooltipProps={{ timeUnit: "s" }}
                    withDots={false}
                    withLegend={false}
                    curveType="linear"
                    xAxisProps={dAxisProps}
                    yAxisProps={GAZE_ANGLE_AXIS_PROPS}
                  />
                </Stack>
              </Paper>
            </Group>
          )}
        </Stack>
      </Group>
    </Stack>
  );
}
